import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { BuilderMessage, detectCurrentStep } from '@/lib/promptBuilderUtils';

interface StepTiming {
  started_at: string;
  completed_at?: string;
  duration_ms?: number;
}

type StepTimings = {
  [key: string]: StepTiming | undefined;
};

export interface LoadedConversation {
  messages: BuilderMessage[];
  initialGoal?: string;
  turnCount: number;
  status: string;
}

interface UseBuilderConversationReturn {
  conversationId: string | null;
  startConversation: (initialGoal?: string) => Promise<string | null>;
  resumeConversation: (existingId: string) => void;
  loadConversation: (id: string) => Promise<LoadedConversation | null>;
  updateConversation: (messages: BuilderMessage[], currentStep: number) => Promise<void>;
  completeConversation: (messages: BuilderMessage[], finalPrompt: string) => Promise<void>;
  abandonConversation: () => Promise<void>;
  linkToScore: (submissionId: string, scoreId: string) => Promise<void>;
}

/**
 * Hook to manage builder conversation persistence and analytics
 */
export function useBuilderConversation(): UseBuilderConversationReturn {
  const [conversationId, setConversationId] = useState<string | null>(null);
  const conversationIdRef = useRef<string | null>(null);
  const stepTimingsRef = useRef<StepTimings>({});
  const currentStepRef = useRef<number>(0);
  const turnCountRef = useRef<number>(0);

  // Keep ref in sync so async callbacks always have the latest ID
  const setConversationIdSync = (id: string | null) => {
    conversationIdRef.current = id;
    setConversationId(id);
  };

  /**
   * Start a new conversation record
   */
  const startConversation = useCallback(async (initialGoal?: string): Promise<string | null> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.warn('[BuilderConversation] No authenticated user');
        return null;
      }

      // Get user's org_id
      const { data: membership } = await supabase
        .from('org_memberships')
        .select('org_id')
        .eq('user_id', user.id)
        .single();

      const now = new Date().toISOString();
      
      // Initialize step 0 timing
      stepTimingsRef.current = {
        step_0: { started_at: now }
      };
      currentStepRef.current = 0;
      turnCountRef.current = initialGoal ? 1 : 0;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const insertData: any = {
        user_id: user.id,
        org_id: membership?.org_id || null,
        status: 'in_progress',
        initial_goal: initialGoal || null,
        turn_count: turnCountRef.current,
        step_timings_json: stepTimingsRef.current,
        messages_json: [],
      };

      const { data, error } = await supabase
        .from('builder_conversations')
        .insert([insertData])
        .select('id')
        .single();

      if (error) {
        console.error('[BuilderConversation] Failed to create conversation:', error);
        return null;
      }

      setConversationIdSync(data.id);
      console.log('[BuilderConversation] Started conversation:', data.id);
      return data.id;
    } catch (err) {
      console.error('[BuilderConversation] Error starting conversation:', err);
      return null;
    }
  }, []);

  /**
   * Update conversation with latest messages and step progress
   */
  const updateConversation = useCallback(async (
    messages: BuilderMessage[],
    currentStep: number
  ): Promise<void> => {
    const id = conversationIdRef.current;
    if (!id) return;

    try {
      const now = new Date().toISOString();

      // Count user messages as turns
      const userMessages = messages.filter(m => m.role === 'user');
      turnCountRef.current = userMessages.length;

      // Track step transitions
      if (currentStep !== currentStepRef.current) {
        const prevStepKey = `step_${currentStepRef.current}` as keyof StepTimings;
        const newStepKey = `step_${currentStep}` as keyof StepTimings;
        
        // Complete previous step timing
        if (stepTimingsRef.current[prevStepKey]) {
          const startTime = new Date(stepTimingsRef.current[prevStepKey]!.started_at).getTime();
          stepTimingsRef.current[prevStepKey]!.completed_at = now;
          stepTimingsRef.current[prevStepKey]!.duration_ms = Date.now() - startTime;
        }
        
        // Start new step timing
        stepTimingsRef.current[newStepKey] = { started_at: now };
        currentStepRef.current = currentStep;
      }

      // Serialize messages for storage
      const messagesJson = messages.map(m => ({
        id: m.id,
        role: m.role,
        content: m.content,
        timestamp: m.timestamp.toISOString(),
      }));

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const updateData: any = {
        messages_json: messagesJson,
        turn_count: turnCountRef.current,
        step_timings_json: stepTimingsRef.current,
      };

      const { error } = await supabase
        .from('builder_conversations')
        .update(updateData)
        .eq('id', id);

      if (error) {
        console.error('[BuilderConversation] Failed to update conversation:', error);
      }
    } catch (err) {
      console.error('[BuilderConversation] Error updating conversation:', err);
    }
  }, []);

  /**
   * Mark conversation as completed with final prompt
   */
  const completeConversation = useCallback(async (
    messages: BuilderMessage[],
    finalPrompt: string
  ): Promise<void> => {
    const id = conversationIdRef.current;
    if (!id) return;

    try {
      const now = new Date().toISOString();

      // Complete the last step timing
      const lastStepKey = `step_${currentStepRef.current}` as keyof StepTimings;
      if (stepTimingsRef.current[lastStepKey]) {
        const startTime = new Date(stepTimingsRef.current[lastStepKey]!.started_at).getTime();
        stepTimingsRef.current[lastStepKey]!.completed_at = now;
        stepTimingsRef.current[lastStepKey]!.duration_ms = Date.now() - startTime;
      }

      // Serialize messages
      const messagesJson = messages.map(m => ({
        id: m.id,
        role: m.role,
        content: m.content,
        timestamp: m.timestamp.toISOString(),
      }));

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const updateData: any = {
        status: 'completed',
        completed_at: now,
        messages_json: messagesJson,
        turn_count: turnCountRef.current,
        step_timings_json: stepTimingsRef.current,
        final_prompt: finalPrompt,
      };

      const { error } = await supabase
        .from('builder_conversations')
        .update(updateData)
        .eq('id', id);

      if (error) {
        console.error('[BuilderConversation] Failed to complete conversation:', error);
      } else {
        console.log('[BuilderConversation] Completed conversation:', id);
      }
    } catch (err) {
      console.error('[BuilderConversation] Error completing conversation:', err);
    }
  }, []);

  /**
   * Mark conversation as abandoned (closed without completing)
   */
  const abandonConversation = useCallback(async (): Promise<void> => {
    const id = conversationIdRef.current;
    if (!id) return;

    try {
      const { error } = await supabase
        .from('builder_conversations')
        .update({
          status: 'abandoned',
          completed_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) {
        console.error('[BuilderConversation] Failed to abandon conversation:', error);
      } else {
        console.log('[BuilderConversation] Abandoned conversation:', id);
      }
    } catch (err) {
      console.error('[BuilderConversation] Error abandoning conversation:', err);
    }
  }, []);

  /**
   * Link conversation to submission and score after scoring completes
   */
  const linkToScore = useCallback(async (
    submissionId: string,
    scoreId: string
  ): Promise<void> => {
    const id = conversationIdRef.current;
    if (!id) return;

    try {
      const { error } = await supabase
        .from('builder_conversations')
        .update({
          submission_id: submissionId,
          score_id: scoreId,
        })
        .eq('id', id);

      if (error) {
        console.error('[BuilderConversation] Failed to link score:', error);
      } else {
        console.log('[BuilderConversation] Linked conversation to score:', scoreId);
      }
    } catch (err) {
      console.error('[BuilderConversation] Error linking score:', err);
    }
  }, []);

  /**
   * Load an existing conversation from the database by ID.
   * Sets the internal conversationId and returns the conversation data.
   */
  const loadConversation = useCallback(async (id: string): Promise<LoadedConversation | null> => {
    try {
      const { data, error } = await supabase
        .from('builder_conversations')
        .select('id, messages_json, initial_goal, turn_count, status, step_timings_json')
        .eq('id', id)
        .single();

      if (error || !data) {
        console.error('[BuilderConversation] Failed to load conversation:', error);
        return null;
      }

      setConversationIdSync(data.id);
      turnCountRef.current = data.turn_count || 0;

      // Restore step timings if available
      if (data.step_timings_json && typeof data.step_timings_json === 'object') {
        stepTimingsRef.current = data.step_timings_json as StepTimings;
      }

      // Parse messages
      const rawMessages = Array.isArray(data.messages_json) ? data.messages_json : [];
      const messages: BuilderMessage[] = rawMessages.map((m: any) => ({
        id: m.id || `msg_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        role: m.role as 'user' | 'assistant',
        content: m.content,
        timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
      }));

      // Detect current step from last assistant message
      const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant');
      if (lastAssistant) {
        const step = detectCurrentStep(lastAssistant.content);
        let detected = 0;
        switch (step) {
          case 'persona': detected = 1; break;
          case 'context': detected = 2; break;
          case 'task': case 'sources': detected = 3; break;
          case 'output_format': detected = 4; break;
        }
        currentStepRef.current = detected;
      }

      console.log('[BuilderConversation] Loaded conversation:', id);
      return {
        messages,
        initialGoal: data.initial_goal || undefined,
        turnCount: data.turn_count || 0,
        status: data.status || 'in_progress',
      };
    } catch (err) {
      console.error('[BuilderConversation] Error loading conversation:', err);
      return null;
    }
  }, []);

  /**
   * Resume an existing conversation (skip creating a new row)
   */
  const resumeConversation = useCallback((existingId: string) => {
    setConversationIdSync(existingId);
    console.log('[BuilderConversation] Resumed conversation:', existingId);
  }, []);

  return {
    conversationId,
    startConversation,
    resumeConversation,
    loadConversation,
    updateConversation,
    completeConversation,
    abandonConversation,
    linkToScore,
  };
}
